<template>
  <CalicoOnboarding 
    :form-data="formData"
    :loading="loading"
    :error="errorMessage"
    @save="save"
  >
    <div class="entity-details">
      <SelectedClaimBusiness 
        :business="business"
        @change="changeBusiness"
      />

      <CalicoFormHeader />

      <CalicoForm
        v-slot="{ error, field }" 
        name="entity" 
        label="Business Entity" 
        rules="required"
      >
        <CalicoDropdown 
          v-model="formData.entity"
          v-bind="field"
          :error="error"
          placeholder="Select entity..."
        >
          <option 
            v-for="option in businessEntities" 
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </CalicoDropdown>
      </CalicoForm>

      <CalicoForm 
        v-if="formData.entity === 'sole_proprietor'" 
        v-slot="{ field, error }" 
        label="SSN"
        name="ssn" 
        rules="required|ssn"
      >
        <CalicoField 
          v-model="formData.ssn" 
          v-bind="field" 
          sensitive
          :error="error" 
          maxlength="9"
          inputmode="numeric"
          autocomplete="off"
          placeholder="123-45-6789" 
          :custom-format="formatSSN" 
        />
      </CalicoForm>

      <CalicoForm 
        v-else 
        v-slot="{ field, error }" 
        label="EIN"
        name="ein" 
        rules="required|ein"
      >
        <CalicoField 
          v-model="formData.ein" 
          v-bind="field"
          sensitive
          :error="error"
          maxlength="9"
          inputmode="numeric"
          autocomplete="off"
          placeholder="12-3456789" 
          :custom-format="formatEIN" 
        />
      </CalicoForm>

      <CalicoForm
        v-slot="{ field }"
        name="dba"
        label="DBA"
      >
        <CalicoField 
          v-model="formData.dba" 
          v-bind="field" 
          placeholder="Enter DBA" 
          maxlength="200" 
        />
      </CalicoForm>

      <CalicoForm 
        v-slot="{ field, error }" 
        name="established_date" 
        label="Established Date" 
        rules="required"
      >
        <CalicoField 
          v-model="formData.established_date" 
          v-bind="field" 
          :error="error"
          :max="today"
          type="date" 
          icon="calendar3" 
        />
      </CalicoForm>

      <CalicoForm
        v-slot="{ field, error }"
        name="company_size"
        label="Number of Employees"
        rules="required"
      >
        <CalicoDropdown
          v-model="formData.company_size"
          v-bind="field"
          :error="error"
          placeholder="Select..."
        >
          <option
            v-for="option in companySizeOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </CalicoDropdown>
      </CalicoForm>

      <CalicoForm
        v-slot="{ field, error }"
        name="annual_revenue"
        label="Annual Revenue"
        rules="required"
      >
        <CalicoField
          v-model="formData.annual_revenue"
          v-bind="field"
          :error="error"
          :only-numbers="true"
          :symbol="'$'"
          :custom-format="formatAndRoundCurrency"
        />
      </CalicoForm>
    </div>
  </CalicoOnboarding>
</template>

<script>
import CalicoFormHeader from "@/components/calico/formHeader/index.vue"
import CalicoOnboarding from "@/components/template/onboarding/index.vue";
import CalicoForm from "@/components/pattern/form/index.vue"
import CalicoField from '@/components/calico/field/index.vue'
import CalicoDropdown from '@/components/calico/dropdown/index.vue';
import SelectedClaimBusiness from '@/components/pattern/selectedClaimBusiness/index.vue'
import { businessEntities, companySizeOptions } from "@/constants";
import services from "@/services";
import { formatEIN, formatSSN } from "@/validator";
import { formatCurrency } from "@/utils/helper";

export default {
  name: 'EntityDetails',
  components: {
    CalicoFormHeader,
    CalicoOnboarding,
    CalicoField,
    CalicoForm,
    CalicoDropdown,
    SelectedClaimBusiness,
  },
  data() {
    return {
      formData: {
        entity: "",
        ein: "",
        ssn: "",
        dba: "",
        established_date: "",
        company_size: "",
        annual_revenue: "",
      },
      business: {},
      businessEntities,
      companySizeOptions,
      loading: true,
      errorMessage: "",
    }
  },
  computed: {
    today() {
      return new Date().toISOString().split('T')[0]
    }
  },
  async mounted() {
    this.business = await services.user.business.get();
    this.formData = {
      entity: this.business.entity,
      ein: this.business.ein,
      ssn: this.business.ssn,
      dba: this.business.dba,
      established_date: this.business.established_date,
      company_size: this.business.company_size ?? "",
      annual_revenue: this.business.annual_revenue ?? "",
    }
    this.loading = false
  },
  methods: {
    async save(next) {
      this.errorMessage = ""
      this.loading = true
      const business = await services.user.business.save(this.formData);
      if (business.id) {
        next()
      } else {
        this.loading = false
        this.errorMessage = business.error
      }
    },
    async changeBusiness() {
      this.loading = true
      this.$router.push('/kyb/claim-business');
    },
    formatAndRoundCurrency(value) {
      const numeric = parseFloat(value);
      const rounded = isNaN(numeric) ? 0 : Math.round(numeric);
      return {
        raw: rounded,
        formatted: rounded.toLocaleString('en-US')
      };
    },
    formatEIN,
    formatSSN,
    formatCurrency,
  }
}
</script>

<style scoped lang="scss">
.entity-details {
  @include stack-space($space-large);
  @include inset-space($space-large);
}
</style>